import { useEffect, useRef, useState, type MutableRefObject } from 'react';

import type { WidgetSizeType } from '../../types';

const JUST_STAMPED_FLOURISH_MS = 800;

const AUTO_REVEAL_MS = 2500;

const REVEAL_START_DELAY_MS = 800;

interface UseStampCardRevealParams {
  justStampedIndex: number | null;
  clearJustStamped: () => void;
  isDialogOpen: boolean;
  sizeType: WidgetSizeType;
  isMobile: boolean;
  current: number;
  isOpeningRef: MutableRefObject<boolean>;
  openWidget: () => void;
  closeWidget: () => void;
}

export function useStampCardReveal({
  justStampedIndex,
  clearJustStamped,
  isDialogOpen,
  sizeType,
  isMobile,
  current,
  isOpeningRef,
  openWidget,
  closeWidget,
}: UseStampCardRevealParams) {
  const [revealedJustStampedIndex, setRevealedJustStampedIndex] = useState<number | null>(null);
  const [displayedCurrent, setDisplayedCurrent] = useState(current);

  const pendingRevealRef = useRef(false);
  const previousJustStampedIndexRef = useRef<number | null>(null);
  const autoRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flourishTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const isNewStamp = justStampedIndex !== null && previousJustStampedIndexRef.current !== justStampedIndex;
    previousJustStampedIndexRef.current = justStampedIndex;
    if (isNewStamp) pendingRevealRef.current = true;

    if (!pendingRevealRef.current || isDialogOpen) return undefined;
    pendingRevealRef.current = false;

    const isAutoRevealing = sizeType === 'small' && isMobile && !isOpeningRef.current;

    const reveal = () => {
      const flourishClearMs = isAutoRevealing ? AUTO_REVEAL_MS : JUST_STAMPED_FLOURISH_MS;

      if (isAutoRevealing) {
        openWidget();
        if (autoRevealTimeoutRef.current) clearTimeout(autoRevealTimeoutRef.current);
        autoRevealTimeoutRef.current = setTimeout(() => {
          if (isOpeningRef.current) closeWidget();
        }, AUTO_REVEAL_MS);
      }

      setRevealedJustStampedIndex(justStampedIndex);

      flourishTimeoutRef.current = setTimeout(() => {
        clearJustStamped();
        setRevealedJustStampedIndex(null);
        setDisplayedCurrent(current);
      }, flourishClearMs);
    };

    if (isAutoRevealing) {
      const revealStartTimeout = setTimeout(reveal, REVEAL_START_DELAY_MS);
      return () => {
        clearTimeout(revealStartTimeout);
        if (flourishTimeoutRef.current) clearTimeout(flourishTimeoutRef.current);
      };
    }

    reveal();
    return () => {
      if (flourishTimeoutRef.current) clearTimeout(flourishTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justStampedIndex, isDialogOpen, sizeType, isMobile]);

  useEffect(() => {
    if (pendingRevealRef.current) return;
    setDisplayedCurrent(current);
  }, [current]);

  useEffect(
    () => () => {
      if (autoRevealTimeoutRef.current) clearTimeout(autoRevealTimeoutRef.current);
    },
    [],
  );

  return { revealedJustStampedIndex, displayedCurrent };
}
